import { EntityBase } from '@provision/entity-types';
import { MouseEvent, useCallback } from 'react';
import { RecordOpenMode } from 'src/graphql';
import { useRecordOpenMode } from 'src/graphql/entities/record-open-mode/record-open-mode';
import { TabEntity, useGlobalTabs } from 'src/utils/hooks';
import { Platform, useScreenDimensions } from 'src/utils/system';

interface UseEntityRecordClickParams<T extends EntityBase> {
  customViewId?: string | null;
  /** metaKey/ctrlKey click opens a tab built from the record; omit when the grid has no tab-open path. */
  buildTabEntity?: (record: T) => TabEntity;
  /** The host's "open full page" navigation; omit when the entity has no full-page view. */
  onOpenFullPage?: (id: T['id'], record: T) => void;
  onOpenRecord: (id: T['id'], record: T) => void;
  onRecordSelect?: (record: T) => void;
}

export const useEntityRecordClick = <T extends EntityBase>({
  customViewId,
  buildTabEntity,
  onOpenFullPage,
  onOpenRecord,
  onRecordSelect,
}: UseEntityRecordClickParams<T>) => {
  const { openInTab } = useGlobalTabs();
  const { currentOpenMode } = useRecordOpenMode();
  const { isMobile } = useScreenDimensions();

  return useCallback(
    (id: T['id'], record: T, event?: MouseEvent) => {
      if (buildTabEntity && (Platform.isMacOS ? event?.metaKey : event?.ctrlKey)) {
        openInTab(buildTabEntity(record), { customViewId: customViewId ?? undefined }, false);
        return;
      }

      if (onOpenFullPage && !isMobile && currentOpenMode === RecordOpenMode.FullPage) {
        onOpenFullPage(id, record);
      } else {
        onOpenRecord(id, record);
      }

      onRecordSelect?.(record);
    },
    [
      buildTabEntity,
      customViewId,
      currentOpenMode,
      isMobile,
      onOpenFullPage,
      onOpenRecord,
      onRecordSelect,
      openInTab,
    ],
  );
};
